import http from '../http';
import { AUTH_API } from '@/config/env';
import { AUTH_KEYS, ROLE } from '@/constants';
import { useCrequestStore } from '@/stores/modules/crequest';
import type { userCredentials } from '@/views/AccountCreation/const';
import { jwtDecode } from 'jwt-decode';

/**
 * @namespace 角色权限模块
 */

/**
 * 获取权限列表
 * @returns {Promise<any>}
 */
export const getAuthInfo: () => Promise<string[]> = () => {
  return new Promise((resolve) => {
    const cRequestStore = useCrequestStore();
    const canShowCollectionRequest = cRequestStore.canShowCollectionRequest;
    let token = localStorage.getItem('token');
    if (token) {
      let decoded: any = jwtDecode(token);
      if (decoded) {
        let roles = decoded['authority'].map((role: any) => role.replace('ROLE_', ''));
        let userId = decoded['userId'];

        let wasteRegisteredKeyPages = [
          AUTH_KEYS.COLLECTION_REQUEST,
          AUTH_KEYS.COLLECTION_REQUEST_CONFIRM,
          AUTH_KEYS.REGISTER_LARGE_GARBAGE,
          AUTH_KEYS.QUOTATION_REQUEST_CONFIRM,
          AUTH_KEYS.BILL_REGISTRATION
        ];
        let wasteOnlyPages = [
          AUTH_KEYS.NEW_REQUEST,
          AUTH_KEYS.ACCOUNT_INFO_REGISTER,
          AUTH_KEYS.INQUIRY
        ];
        let masterPages = [
          AUTH_KEYS.MASTER_ADMIN,
          AUTH_KEYS.ACCOUNT_MASTER,
          AUTH_KEYS.WASTE_MASTER,
          AUTH_KEYS.UNIT_OF_MEASURE_MASTER,
          AUTH_KEYS.PACKAGING_MASTER,
          AUTH_KEYS.ACCOUNT_INFO,
          AUTH_KEYS.ACCOUNT_SELECTION,
          AUTH_KEYS.DISPOSAL_SITE_MASTER,
          AUTH_KEYS.INQUIRY_ADMIN
        ];
        let wasteAndTransportPages = [AUTH_KEYS.CHANGE_PASSWORD];
        let transportOnlyPages = [
          AUTH_KEYS.BUSINESS_DATE_TIME_SETTINGS,
          AUTH_KEYS.DISPOSAL_UNIT_PRICE_SETTING,
          AUTH_KEYS.LICENSE_MANAGEMENT
        ];
        let defaultTransportOnlyPages = [AUTH_KEYS.WASTE_HANDLING_UNIT_PRICE_SETTING];

        if (roles.includes(ROLE.ADMIN)) {
          let data = Object.values(AUTH_KEYS).filter(
            (item) =>
              !wasteOnlyPages.includes(item) &&
              !wasteAndTransportPages.includes(item) &&
              !transportOnlyPages.includes(item) &&
              !defaultTransportOnlyPages.includes(item)
          );

          if (roles.includes(ROLE.WASTE)) {
            if(!decoded.subNum || !decoded.ediKey || !canShowCollectionRequest)
            {
              data = data.concat(wasteOnlyPages, wasteAndTransportPages).filter(
                (item) => !wasteRegisteredKeyPages.includes(item)
              );
            }
           else{
            data = data.concat(wasteOnlyPages, wasteAndTransportPages);
           }
          }
          if (roles.includes(ROLE.TRANSPORT)) {
            data = data.concat(wasteAndTransportPages, transportOnlyPages);
            if (userId === 20000) data = data.concat(defaultTransportOnlyPages);
          }

          resolve([...new Set(data)]); // Remove duplicates
        } else if (roles.includes(ROLE.WASTE)) {
          if (!decoded.subNum || !decoded.ediKey || !canShowCollectionRequest) {
            const data = Object.values(AUTH_KEYS).filter(
              (item) =>
                !masterPages.includes(item) &&
                !wasteRegisteredKeyPages.includes(item) &&
                !transportOnlyPages.includes(item) &&
                !defaultTransportOnlyPages.includes(item)
            );
            resolve(data);
          } else {
            const data = Object.values(AUTH_KEYS).filter(
              (item) =>
                !masterPages.includes(item) &&
                !transportOnlyPages.includes(item) &&
                !defaultTransportOnlyPages.includes(item)
            );
            resolve(data);
          }
        } else {
          let data = Object.values(AUTH_KEYS).filter(
            (item) =>
              !masterPages.includes(item) &&
              !wasteOnlyPages.includes(item) &&
              !defaultTransportOnlyPages.includes(item)
          );
          if (userId === 20000) data = data.concat(defaultTransportOnlyPages);
          resolve(data);
        }
      }
    } else {
      resolve([]);
    }
  });
};

export const login = (email: string, password: string) => {
  return http.post(`/auth/login`, { email, password }, { noToken: true });
};

export const resetPass = (pass: string, token: string) => {
  return http.post(`/auth/password/reset`, { pass, token }, { noToken: true });
};

export const changePass = (oldPass: string, newPass: string) => {
  return http.post(`/auth/password/change`, { oldPass, newPass });
};

export const sendMail = (email: string) => {
  return http.post(`/auth/password/send-email`, { email });
};

export const signUp = (userData: userCredentials) => {
  return http.post(`/auth/signup`, userData, { noToken: true });
};

export const updateAccountStatus = async (
  token: string
): Promise<{ result: boolean; message?: string }> => {
  const response = await http.put<{ result: boolean; message?: string }>(`/auth/account/active`, {
    token
  });
  return response;
};

export const refreshToken = () => {
  return http.post(`/auth/refresh-token`);
};

export const checkTokenInMail = (token: string) => {
  return http.get(`/auth/link/check`, {
    params: { token }
  });
};

/**
 * 获取用户信息
 * @returns {Promise<any>}
 */
export const getUserInfo = () => http.get(`${AUTH_API}/userInfo`);
